import React, {FC, useEffect, useState} from "react"
import moment from "moment"
import styled, {css} from "styled-components"

import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {Body, H1, H2, H4, H6, Label} from "@irati/ui/src/Typography"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useGetPaymentLink, useConfirmSignUpPayment, useGetQuoteQuery} from "@irati/signup/hooks"
import {Card, Hr} from "@irati/ui/src/Layout"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Spacing from "@irati/ui/src/Spacing"
import {hangPromise} from "@irati/utils/promise"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import mc from "@irati-web/payments/src/images/mastercard.svg"
import visa from "@irati-web/payments/src/images/visa.svg"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import ga4Tracking from "@irati-web/tracking"

import {UseQueriesResult} from "@irati/utils/services/apollo"
import {SignUpPaymentMethodType, SignUp, SignUpBillingPeriod, SignUpStage, PaymentCurrencyType} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import Spinner from "@irati/ui/src/Loading/Spinner"
import PaymentSuccess from "../../components/SignUp/Cards/PaymentSuccess"
import PaymentError from "../../components/SignUp/Cards/PaymentError"
import ConfirmPaymentError from "../../components/SignUp/Cards/ConfirmPaymentError"
import SaveAndProgressFooter from "../../components/SignUp/SaveAndProgressFooter"
import SaveAndProgressMobile from "../../components/SignUp/SaveAndProgressMobile"
import TermsAndConditions from "../../components/SignUp/TermsAndConditions"
import {TermsWrapper, StyledCard, FullWidth, StyledFlex, DesktopOnlyWrapper, MobileOnlyWrapper} from "../../components/SignUp/styled"

const StyledDiv = styled.div`
  #z_hppm_iframe {
    background: white;
  }
`

const Image = styled.img`
  height: 30px;
  width: 40px;
`

const FlexWidth = styled(Flex)`
  width: 400px;
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      width: 100%;
    `}
  `};
`

const StyledButton = styled(Button)`
  ${({theme, isSelected}) => css`
    ${isSelected &&
    `
       color: ${theme.colors.utility.white};
    background-color: ${theme.colors.primary2};
    text-decoration: none;

    path {
      fill: ${theme.colors.utility.white};
    }
    `}
  `};
`

const VisaContainer = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
      flex-direction: column;
      gap: 4px;
      margin-bottom: 16px;
    `}
  `};
`

interface Props {
  signUp: UseQueriesResult<SignUp>
  onEditPlan: () => void
}

const SelfSignUpPayment: FC<Props> = ({signUp, onEditPlan}) => {
  const theme = useTheme()
  const [showTermsModal, setTermsModal] = useState(false)
  const [load, setLoad] = useState<Promise<void> | null>(hangPromise())
  const [showAlert, setShowAlert] = useState(false)
  const [showError, setShowError] = useState(false)
  const [confirmPaymentError, setConfirmPaymentError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(signUp?.data?.stage === SignUpStage.SignUpStagePayment)
  const [agreementChecked, setAgreementChecked] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<SignUpPaymentMethodType>(SignUpPaymentMethodType.PaymentMethodDirectDebit)
  const {data, refetch} = useGetPaymentLink({signupId: signUp?.data.uuid, method: paymentMethod}, {fetchPolicy: "network-only"})
  const [confirmSignUpPayment] = useConfirmSignUpPayment()
  const [logEvent, setLogEvent] = useState(true)
  const getQuoteQuery = useGetQuoteQuery(
    {
      uuid: signUp?.data.uuid
    },
    {
      fetchPolicy: "cache-and-network"
    }
  )
  const [currencyCode, setCurrencyCode] = useState(PaymentCurrencyType.Gbp)

  const {formatMessage} = useIntl()

  useEffect(() => {
    ga4Tracking.trackEventWithStore("payment_viewed", "sbs_params", {
      step: "5",
      email: signUp?.data?.enquiry?.emailAddress,
      page: "payment_page"
    })

    if (signUp?.data?.stage === SignUpStage.SignUpStageComplete) {
      return setShowSuccess(true)
    }
  }, [])

  useEffect(() => {
    if (data) {
      fetchPaymentLink()
    }
  }, [data])

  useEffect(() => {
    if (getQuoteQuery?.data?.currency) {
      setCurrencyCode(getQuoteQuery.data.currency)
    }
  }, [getQuoteQuery?.data])

  useEffect(() => {
    refetch()
  }, [paymentMethod])

  const handleReset = () => {
    setShowError(false)
    refetch()
  }

  const fetchPaymentLink = () => {
    const {tenantId, key, token, signature, uri, pageId} = data
    setLoad(hangPromise())

    const params = {
      tenantId,
      id: pageId,
      token,
      signature,
      style: "inline",
      key,
      submitEnabled: "true",
      url: uri
    }
    window.Z.setEventHandler("onloadCallback", function () {
      console.warn("HPM page is loaded.")
    })
    window.Z.setEventHandler("agreement_checked", function () {
      setAgreementChecked(true)
    })
    window.Z.setEventHandler("agreement_unchecked", function () {
      setAgreementChecked(false)
    })

    const callback = (response) => {
      if (response?.success) {
        completePayment()
        confirmSignUpPayment({
          variables: {
            signupId: signUp?.data?.uuid,
            signUpPayment: {
              signature,
              paymentMethod,
              paymentMethodId: response?.refId
            }
          }
        })
          .then(() => {
            setShowSuccess(true)
            conversionComplete()
          })
          .catch(() => {
            setConfirmPaymentError(true)
          })
      } else {
        setShowError(true)
      }
    }
    window.Z.render(params, {}, callback)
    setTimeout(function () {
      setLoad(null)
    }, 600)
  }

  const startPayment = () => {
    if (logEvent) {
      ga4Tracking.trackEventWithStore("payment_initiated", "sbs_params", {
        step: "5.1",
        payment_start_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
        email: signUp?.data?.enquiry?.emailAddress,
        page: "payment_page"
      })
      setLogEvent(false)
    }

    if (termsAccepted) {
      submitPayment()
      window.Z.submit()
    } else {
      setShowAlert(true)
    }
  }

  const submitPayment = () => {
    ga4Tracking.trackEventWithStore("payment_submitted", "sbs_params", {
      step: "5.2",
      payment_type: paymentMethod === SignUpPaymentMethodType.PaymentMethodCreditCard ? "card" : "direct_debit",
      payment_submit_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
      email: signUp?.data?.enquiry?.emailAddress,
      page: "payment_page"
    })
  }

  const completePayment = () => {
    ga4Tracking.trackEventWithStore("payment_completed", "sbs_params", {
      step: "5.3",
      payment_type: paymentMethod === SignUpPaymentMethodType.PaymentMethodCreditCard ? "card" : "direct_debit",
      payment_complete_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
      email: signUp?.data?.enquiry?.emailAddress,
      page: "payment_page"
    })
  }

  const conversionComplete = () => {
    ga4Tracking.trackEventWithStore("conversion_to_customer", "sbs_params", {
      step: "5.4",
      email: signUp?.data?.enquiry?.emailAddress,
      page: "payment_page",
      customer_id: "userId"
    })
  }

  const handleTermsClick = () => {
    setTermsModal(true)
  }

  const handleModalClose = (accepted: boolean) => {
    setTermsModal(false)
    setTermsAccepted(accepted)
  }

  const handleTermsAccepted = () => {
    setTermsAccepted(true)
    setShowAlert(false)
  }

  const handleDirectDebitClick = () => {
    setAgreementChecked(false)
    setPaymentMethod(SignUpPaymentMethodType.PaymentMethodDirectDebit)
  }

  if (showSuccess) {
    return <PaymentSuccess />
  }

  if (showError) {
    return <PaymentError onClick={handleReset} />
  }

  if (confirmPaymentError) {
    return <ConfirmPaymentError />
  }

  const isQuarterly = signUp?.data.enquiry.option.billingPeriod === SignUpBillingPeriod.BillingPeriodQuarterly
  const totalAnnualCost = isQuarterly
    ? `(${formatCurrency({value: signUp.data.enquiry.option.pricePerMonth * 3 * signUp.data.enquiry.licences, currencyCode})} x 4)`
    : `(${formatCurrency({value: signUp.data.enquiry.option.pricePerMonth * signUp.data.enquiry.licences, currencyCode})} x 12)`
  const paymentDueToday = isQuarterly
    ? `${formatCurrency({value: signUp.data.enquiry.option.pricePerMonth * 3 * signUp.data.enquiry.licences, currencyCode})}`
    : `${formatCurrency({value: signUp.data.enquiry.option.pricePerMonth * signUp.data.enquiry.licences, currencyCode})}`

  return (
    <StyledCard maxWidth="1200px">
      <StyledFlex gap={4}>
        <FlexWidth>
          <Flex direction="column" justifyContent="space-between" width="100%">
            <div>
              <Flex justifyContent="space-between" marginBottom={2}>
                <H2 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.payment.planDetails" />
                </H2>
                <Button tertiary small onClick={onEditPlan}>
                  <FormattedMessage id="platform.selfSignUp.payment.editPlanDetails" />
                </Button>
              </Flex>
              <DesktopOnlyWrapper>
                <Label color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.payment.noLicenses" />
                </Label>
                <Hr margin={[0, 0.5]} />
                <H4 marginBottom={2} color={theme.colors.primary2}>
                  {signUp.data.enquiry.licences}
                </H4>
                <Label color={theme.colors.primary2} marginBottom={1}>
                  <FormattedMessage id="platform.selfSignUp.payment.licensesHint" />
                </Label>
              </DesktopOnlyWrapper>

              {signUp?.data.enquiry.option.lengthOfContractInMonths === 12 && (
                <DesktopOnlyWrapper>
                  <Label marginTop={2} display="block" color={theme.colors.primary2}>
                    <FormattedMessage id="platform.selfSignUp.payment.annualCost" />
                  </Label>
                  <Hr margin={[0, 0.5]} />
                  <Flex alignItems="center" marginBottom={2} gap={1}>
                    <H2 color={theme.colors.primary2}>
                      {formatCurrency({
                        value: signUp.data.enquiry.option.pricePerMonth * 12 * signUp.data.enquiry.licences,
                        currencyCode
                      })}
                    </H2>
                    <Body color={theme.colors.primary2}>{totalAnnualCost}</Body>
                  </Flex>
                </DesktopOnlyWrapper>
              )}

              <Card bgColor={theme.colors.primary1_50} padding={1} marginBottom={1}>
                <Label display="block" color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.payment.dueToday" />
                </Label>
                <Hr margin={[0, 0.5]} />
                <H2>{paymentDueToday}</H2>
              </Card>
              <DesktopOnlyWrapper>
                <Label display="block" color={theme.colors.primary2} marginTop={2}>
                  <FormattedMessage id="platform.selfSignUp.payment.cancelDetails" />
                </Label>
              </DesktopOnlyWrapper>
            </div>
            <DesktopOnlyWrapper>
              <Label display="block" color={theme.colors.primary2} marginTop={1}>
                <FormattedMessage id="platform.selfSignUp.payment.annualSubscription" />
              </Label>
            </DesktopOnlyWrapper>
          </Flex>
        </FlexWidth>
        <FullWidth>
          <H1 color={theme.colors.primary2} marginBottom={2}>
            <FormattedMessage id="platform.selfSignUp.payment.title" />
          </H1>
          <VisaContainer justifyContent="space-between" marginBottom={1}>
            <H6 color={theme.colors.primary2}>
              <FormattedMessage id="platform.selfSignUp.payment.subTitle" />
            </H6>
            <div>
              <Image src={visa} alt="visa" />
              <Image src={mc} alt="mastercard" />
            </div>
          </VisaContainer>
          <Flex justifyContent="space-between" gap={1} marginBottom={2}>
            <StyledButton
              full
              onClick={handleDirectDebitClick}
              isSelected={paymentMethod === SignUpPaymentMethodType.PaymentMethodDirectDebit}>
              <FormattedMessage id="platform.selfSignUp.payment.directDebit" />
            </StyledButton>
            <StyledButton
              full
              onClick={() => setPaymentMethod(SignUpPaymentMethodType.PaymentMethodCreditCard)}
              isSelected={paymentMethod === SignUpPaymentMethodType.PaymentMethodCreditCard}>
              <FormattedMessage id="platform.selfSignUp.payment.creditCard" />
            </StyledButton>
          </Flex>
          <Spacing marginTop={3}>
            <Loading load={load} Loader={() => <Spinner />} />
          </Spacing>
          <StyledDiv id="zuora_payment"></StyledDiv>
          {showAlert && <Alert type={AlertTypes.error} title="platform.selfSignUp.payment.alertTitle" nonDismissable />}
          <TermsWrapper alignItems="center" height="42px" padding={[0, 3]} margin={[2, 0.5]}>
            <Icon icon={termsAccepted ? IconTypes.circleCheckFill : IconTypes.document} marginRight size={termsAccepted ? 18 : 16} />
            <Button tertiary cta onClick={handleTermsClick}>
              <FormattedMessage
                id={termsAccepted ? "platform.selfSignUp.payment.termsAccepted" : "platform.selfSignUp.payment.termsNotAccepted"}
              />
            </Button>
            <Icon icon={IconTypes.angleRight} marginLeft size={24} marginTop />
          </TermsWrapper>
          <TermsAndConditions
            show={showTermsModal}
            signUp={signUp?.data}
            termsAccepted={termsAccepted}
            onAccept={handleTermsAccepted}
            onHide={handleModalClose}
          />
        </FullWidth>
      </StyledFlex>
      <SaveAndProgressFooter
        disabledSave={
          paymentMethod === SignUpPaymentMethodType.PaymentMethodDirectDebit ? !termsAccepted || !agreementChecked : !termsAccepted
        }
        onConfirm={startPayment}
        confirmText={formatMessage({id: "platform.selfSignUp.payment.confirmText"})}
      />
      <SaveAndProgressMobile
        disabledSave={
          paymentMethod === SignUpPaymentMethodType.PaymentMethodDirectDebit ? !termsAccepted || !agreementChecked : !termsAccepted
        }
        onConfirm={startPayment}
        confirmText={formatMessage({id: "platform.selfSignUp.payment.confirmText"})}
      />
      <MobileOnlyWrapper>
        <Label display="block" color={theme.colors.primary2} marginBottom={2}>
          <FormattedMessage id="platform.selfSignUp.payment.annualSubscription" />
        </Label>
      </MobileOnlyWrapper>

      <MobileOnlyWrapper>
        <Label color={theme.colors.primary2}>
          <FormattedMessage id="platform.selfSignUp.payment.noLicenses" />
        </Label>
        <Hr margin={[0, 0.5]} />
        <H4 marginBottom={2} color={theme.colors.primary2}>
          {signUp.data.enquiry.licences}
        </H4>
        <Label color={theme.colors.primary2} marginBottom={1}>
          <FormattedMessage id="platform.selfSignUp.payment.licensesHint" />
        </Label>
      </MobileOnlyWrapper>

      <MobileOnlyWrapper>
        <Body weight="semibold" marginTop={2} marginBottom={2} display="block">
          {signUp?.data.enquiry.option.lengthOfContractInMonths === 12 ? (
            <FormattedMessage id="platform.selfSignUp.payment.annual" />
          ) : (
            <FormattedMessage id="platform.selfSignUp.payment.monthly" />
          )}
        </Body>
        <Label>
          <FormattedMessage id="platform.selfSignUp.payment.total" />
        </Label>
        <Hr margin={[0, 0.5]} />
        <Flex alignItems="center" marginBottom={2} gap={1}>
          <H2 color={theme.colors.primary2}>
            {formatCurrency({
              value: signUp.data.enquiry.option.pricePerMonth * 12 * signUp.data.enquiry.licences,
              currencyCode
            })}
          </H2>
          <Body color={theme.colors.primary2}>
            {`(${formatCurrency({value: signUp.data.enquiry.option.pricePerMonth * 3 * signUp.data.enquiry.licences, currencyCode})} x 4)`}
          </Body>
        </Flex>
        <Label>
          <FormattedMessage id="platform.selfSignUp.payment.renewalDate" />
        </Label>
      </MobileOnlyWrapper>
    </StyledCard>
  )
}

export default SelfSignUpPayment
